// Which of the eight top-level views `<App/>` shows — as one function, so the
// precedence is stated once and testable without rendering the whole SPA.
//
// The load-bearing rule is the first line: an EXPIRED session outranks a cached
// `me`. TanStack keeps the last successful data on a failed refetch, so a gate
// that only asked "do we have an account?" answered yes forever once the cookie
// died — the app stayed mounted over a credential the API had stopped
// accepting, every panel showing its own 401. The latch (lib/session-expiry.ts)
// is the only thing that can say otherwise, so it is read first.
//
// The second is the WAITLIST, and it outranks consent because the SERVER's
// ladder does — stated in three places: `sessionScopes` calls approval "the
// outermost gate — approval precedes everything"
// (`apps/api/src/middleware/auth.ts`), `resolveApiToken` runs the rungs in that
// order, and `accessView` (`packages/mcp/src/access-view.ts`) says why an unapproved
// human cannot usefully be sent to a Terms page for an account they may never
// be admitted to. Without this rung a capped session landed in the full shell
// and every panel 403'd on its own.
//
// Everything below it is the pre-existing order, unchanged: a 401 on `/v1/me`
// itself is "signed out" (→ login), while a 500 / network / CORS failure is an
// OUTAGE — routing that to <Login/> would hide an incident behind a sign-in
// screen and invite the user to "fix" it by re-authenticating.

import { isUnauthorized } from './api';
import { INVITE_PATH } from './invitations';
import {
  isWaitlisted,
  type Me,
  needsConsent,
  needsCrossBorderConsent,
  needsRegion,
} from './session';

/** The eight mutually-exclusive top-level views. */
export type AppGate =
  | 'login'
  | 'checking'
  | 'waitlisted'
  | 'consent'
  | 'region'
  | 'cross_border'
  | 'app'
  | 'outage';

/** Everything the gate decides on: the latch plus the `/v1/me` query's state. */
export type AppGateInput = {
  /** `sessionExpired()` — any `/v1` call has been refused for want of a credential. */
  readonly expired: boolean;
  /** `me.isLoading` — first probe in flight, nothing to show yet. */
  readonly loading: boolean;
  /** `me.data` — the signed-in account (survives a failed refetch). */
  readonly me: Me | undefined;
  /** `me.error` — why the probe failed, if it did. */
  readonly error: unknown;
  /**
   * The live pathname — the waitlist exemption's one input, and REACTIVE.
   *
   * It starts as `window.location.pathname` (the location at load, which is how
   * a waitlisted session reaches `/invite` at all: the link in the invitation
   * mail, or the post-sign-in `return_to` bounce,
   * `apps/api/src/github-auth/flow.ts`) and is then kept current by the app
   * Router itself — `<App/>` sits OUTSIDE the Router and solid-router navigates
   * via pushState, which fires no event, so the Router's own `useLocation()`
   * reports up into the signal `<App/>` reads (`App.tsx`).
   *
   * Reading it ONCE was a hole, not a nuance. `/invite` falls through to the app
   * shell, so a waitlisted person who lands there and does not get approved can
   * click any nav link and be inside the console this gate exists to keep them
   * out of — every panel 403ing on its own, until the next full page load. Two
   * reachable ways to be in that state, neither exotic: a FAILED redeem (the
   * screen stays put, `lib/invite-resume.ts` maps every refusal to `stay`), and a
   * SUCCESSFUL one on an `org_member` invitation, which inserts a membership and
   * deliberately does NOT stamp `users.approved_at`
   * (`packages/db/src/queries/invitations.ts`) — so the person is genuinely in an
   * org and genuinely still waitlisted, and the resume hop navigates them off
   * `/invite` itself.
   */
  readonly path: string;
};

/** Resolve the top-level view. See the file header for why the order is this order. */
export function resolveAppGate(state: AppGateInput): AppGate {
  if (state.expired) return 'login';
  if (state.loading) return 'checking';
  if (state.me) {
    // ONE exemption, and it is the act that LIFTS the wall rather than a
    // courtesy: `POST /v1/invitations/accept` rides `read:account` precisely so
    // a waitlisted session can reach it (`apps/api/src/routes/invitations.ts`),
    // and redeeming stamps `users.approved_at`
    // (`packages/db/src/queries/invitations.ts`). Walling `/invite` would make
    // the platform-invite path unreachable and the invite-only posture
    // undeliverable.
    //
    // It falls THROUGH to the app rather than getting a Router of its own, and
    // that is load-bearing. `routes/Invite.tsx` awaits `me.refetch()` and THEN
    // navigates to the resumed funnel; a second Router here would be unmounted
    // by that refetch's `approved: true` flipping this gate BEFORE the navigate
    // ran, the app Router would remount <Invite/> at `/invite`, the spent
    // single-use token would be re-POSTed, and someone who just got in would be
    // told they had already redeemed their invitation (`lib/invite-resume.ts`
    // maps `already_accepted` to `stay`). ONE Router mounted across the flip is
    // what makes the resume land.
    if (isWaitlisted(state.me) && state.path !== INVITE_PATH) return 'waitlisted';
    // ToS, then the region question, then the separate cross-border consent
    // (#5881): the region decides whether that consent applies at all, and it is
    // NEVER bundled with the ToS checkbox, so each is its own screen, in order.
    if (needsConsent(state.me)) return 'consent';
    if (needsRegion(state.me)) return 'region';
    return needsCrossBorderConsent(state.me) ? 'cross_border' : 'app';
  }
  if (state.error !== undefined && state.error !== null && !isUnauthorized(state.error)) {
    return 'outage';
  }
  return 'login';
}
